// Client-side formula evaluator for formula columns.
//
// A formula is a single JavaScript expression that references column values via
// {{Column Label}} placeholders, e.g.
//   {{Current Company}}?.toLowerCase()?.includes("energy") ? "Yes" : ""
//
// We evaluate it in the user's OWN browser against their OWN data — the same
// trust model as a spreadsheet formula — so there is no server-side code
// execution. A lightweight blocklist guards against accidental/obvious escapes;
// the generator is also constrained to a small grammar.

// Tokens that must never appear in a formula (defense-in-depth).
const BLOCKED = /\b(window|document|globalThis|self|eval|Function|require|import|fetch|XMLHttpRequest|WebSocket|constructor|__proto__|prototype|localStorage|sessionStorage|process|alert)\b|`|=>/;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Distinct column labels referenced inside {{ }}, in first-seen order.
export function extractFormulaRefs(formula: string): string[] {
  const out: string[] = [];
  const re = /\{\{([^}]+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(formula)) !== null) {
    const label = m[1].trim();
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
}

// Compile a formula into a function of an ordered `values` array (aligned with
// `refs`). Throws if the formula uses a blocked token or fails to compile.
// The returned function never throws — a per-row evaluation error yields "".
export function compileFormula(
  formula: string,
  refs: string[]
): (values: any[]) => any {
  // Check for blocked tokens on CODE only — strip string literals first so a
  // legitimate value like 'self employed' doesn't trip the `self` guard.
  const codeOnly = formula.replace(/'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"/g, "''");
  if (BLOCKED.test(codeOnly)) {
    throw new Error('Formula contains a disallowed keyword or syntax.');
  }
  let expr = formula;
  refs.forEach((label, i) => {
    const re = new RegExp('\\{\\{\\s*' + escapeRegExp(label) + '\\s*\\}\\}', 'g');
    expr = expr.replace(re, `cols[${i}]`);
  });
  // Any leftover / unknown placeholder resolves to undefined.
  expr = expr.replace(/\{\{[^}]*\}\}/g, 'undefined');

  let fn: (cols: any[]) => any;
  try {
    // eslint-disable-next-line no-new-func
    fn = new Function('cols', '"use strict"; return (' + expr + ');') as (cols: any[]) => any;
  } catch (e: any) {
    throw new Error('Invalid formula: ' + (e?.message || 'could not compile'));
  }
  return (values: any[]) => {
    try {
      const result = fn(values);
      return result;
    } catch {
      return '';
    }
  };
}

// Normalise a computed value to the string we store in custom_data.
export function formulaValueToString(v: any): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  return String(v);
}
